import React, { useEffect, useState } from "react"
import { Modal } from "../../../NewModal"
import { Flex } from "rebass"
import { InlineSpan } from "../../../inline-span/InlineSpan"
import { BodyTextRegular } from "../../../typography/text"
import { css, useTheme } from "@emotion/react"
import { FormProvider, useForm } from "react-hook-form"
import { TextInput } from "../../../forms/inputs/TextInput"
import { ButtonPrimary } from "../../../button/buttons"
import { yupResolver } from "@hookform/resolvers/yup"
import { AuthenticateRequest } from "../../../../models/email-authenticate-request"
import { ErrorBox } from "../../../errors/ErrorBox"
import { useTranslation } from "gatsby-plugin-react-i18next"
import { extractAxiosError } from "../../../../util/error-handler"
import { errorCodes } from "../../../../services/error-codes"
import { translationKeys } from "../../../../util/translationKeys"
import { useLoginSchemaProperties } from "../../../forms/validation-schemas/use-login-schema-properties"
import { useAuthModals } from "../../../../context/AuthModalsContext"
import { useAccount } from "../../../../context/AccountContext"

const modalStyle = css`
  flex-direction: column;
  align-items: space-between;
  gap: 24px;
  width: 100%;
`

const formStyle = css`
  align-items: center;
  align-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  gap: 8px;
`

const bottomStyle = css`
  flex-direction: column;
  width: 100%;
  gap: 24px;
  padding-top: 16px;
`
const signUpLinkStyle = css`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

export const LogInModal = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  const [error, setError] = useState<string>("")
  const [isSigningIn, setIsSigningIn] = useState(false)

  const { signinUser } = useAccount()
  const { visibleAuthModal, setVisibleAuthModal } = useAuthModals()

  const loginSchemaProperties = useLoginSchemaProperties()

  const methods = useForm<AuthenticateRequest>({
    mode: "onChange",
    resolver: yupResolver(loginSchemaProperties),
  })
  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (
      visibleAuthModal === "login" ||
      visibleAuthModal === "forgot-password"
    ) {
      return
    }
    reset()
    setError("")
  }, [visibleAuthModal])

  const getLoginErrorDescription = (code: string) => {
    switch (code.toLowerCase()) {
      case errorCodes.UNKNOWN_USER_OR_WRONG_PASSWORD:
        return t(translationKeys.webLoginFailedLogin)
      default:
        return t(translationKeys.webLoginFailedLogin)
    }
  }

  const onSubmit = async (payload: AuthenticateRequest) => {
    setIsSigningIn(true)
    try {
      await signinUser(payload)
    } catch (error: any) {
      const errorCode = extractAxiosError({ error })
      const errorMessage = getLoginErrorDescription(errorCode)
      setError(errorMessage)
    }
    setIsSigningIn(false)
  }

  return (
    <Modal
      title={`${t(translationKeys.comboWelcomeLogind)}`}
      subtitle={`${t(translationKeys.comboResetPasswordTitle)}`}
      subtitleSpan={
        <InlineSpan
          onClick={() => {
            setVisibleAuthModal("forgot-password")
          }}
        >
          {t(translationKeys.webResetPasswordCta)}
        </InlineSpan>
      }
      isVisible={visibleAuthModal === "login"}
      onClose={() => setVisibleAuthModal(undefined)}
    >
      <Flex css={modalStyle}>
        <FormProvider {...methods}>
          <form>
            <Flex css={formStyle}>
              <TextInput
                label={`${t(translationKeys.comboCreateaccountEmail)}`}
                placeholder={`${t(translationKeys.comboSignupEmailHint)}`}
                name="username"
                required={true}
              />
              <TextInput
                label={`${t(translationKeys.comboCreateaccountPassword)}`}
                placeholder={`${t(translationKeys.comboLoginPasswordHint)}`}
                name="password"
                type="password"
                required={true}
              />
              <Flex css={bottomStyle}>
                {error && <ErrorBox title={error} />}
                <ButtonPrimary
                  disabled={false}
                  type="button"
                  backgroundColor={theme.colors.ctaMain}
                  hoverColor={theme.colors.ctaSecondaryOne}
                  color={theme.colors.white}
                  loading={isSigningIn}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t(translationKeys.comboWelcomeLogind)}
                </ButtonPrimary>
                <Flex css={signUpLinkStyle}>
                  <BodyTextRegular color={theme.colors.primary}>
                    {t(translationKeys.webLoginNoaccount)}{" "}
                    <InlineSpan onClick={() => setVisibleAuthModal("register")}>
                      {t(translationKeys.startCreateProfile)}
                    </InlineSpan>
                  </BodyTextRegular>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  )
}
